import { computed } from 'vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { syncService } from '@/services/syncService.js'

/**
 * Composable for showing "laatst gesynchroniseerd" timestamps
 * @param {string|string[]} syncTypes - One or more sync types to track (takes the most recent)
 */
export function useLastSynced(syncTypes) {
  const queryClient = useQueryClient()
  const types = Array.isArray(syncTypes) ? syncTypes : [syncTypes]

  const { data: allTimestamps } = useQuery({
    queryKey: ['last-synced'],
    queryFn: syncService.getLastSynced,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false
  })

  const lastSyncedAt = computed(() => {
    if (!allTimestamps.value) return null
    let latest = null
    for (const type of types) {
      const ts = allTimestamps.value[type]
      if (ts && (!latest || new Date(ts) > new Date(latest))) {
        latest = ts
      }
    }
    return latest
  })

  const lastSyncedLabel = computed(() => {
    if (!lastSyncedAt.value) return null
    return formatRelative(new Date(lastSyncedAt.value))
  })

  const lastSyncedFull = computed(() => {
    if (!lastSyncedAt.value) return null
    const d = new Date(lastSyncedAt.value)
    return d.toLocaleString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['last-synced'] })
  }

  return { lastSyncedAt, lastSyncedLabel, lastSyncedFull, invalidate }
}

function formatRelative(date) {
  const now = new Date()
  const diffMs = now - date
  const diffMin = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)

  if (diffMin < 1) return 'Zojuist'
  if (diffMin < 60) return `${diffMin} min geleden`
  if (diffHours < 24) return `${diffHours} uur geleden`

  const time = date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const dateDay = new Date(date)
  dateDay.setHours(0, 0, 0, 0)

  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  if (dateDay.getTime() === today.getTime()) return `Vandaag om ${time}`
  if (dateDay.getTime() === yesterday.getTime()) return `Gisteren om ${time}`

  const month = date.toLocaleString('nl-NL', { month: 'short' })
  const day = date.getDate()

  if (date.getFullYear() === now.getFullYear()) {
    return `${day} ${month} ${time}`
  }

  return `${day} ${month} ${date.getFullYear()} ${time}`
}
